import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCanvases } from '../../../app/store/slices/canvasSlice';

const CanvasContext = createContext();

// Stable empty array so effect deps don't change when store has no canvases yet
const EMPTY_CANVASES = [];

export const CanvasProvider = ({ children }) => {
  const raw = useSelector(selectCanvases);
  const ownedCanvases = Array.isArray(raw) ? EMPTY_CANVASES : (raw?.owned ?? EMPTY_CANVASES);
  const [selectedCanvas, setSelectedCanvas] = useState(() => {
    const savedCanvas = JSON.parse(localStorage.getItem('selectedCanvas'));
    if (savedCanvas?.id) return savedCanvas;

    return {
      id: null,
      type: 'private',
      name: '',
      currency: '',
    };
  });

  const selectedCanvasRef = useRef(selectedCanvas);
  selectedCanvasRef.current = selectedCanvas;

  useEffect(() => {
    if (selectedCanvas.id && ownedCanvases.length > 0) {
      const updatedCanvas = ownedCanvases.find(canvas => canvas._id === selectedCanvas.id);
      if (updatedCanvas) {
        const newSelectedCanvas = {
          id: updatedCanvas._id,
          type: updatedCanvas.type,
          name: updatedCanvas.name,
          currency: updatedCanvas.currency || '',
        };
        const current = selectedCanvasRef.current;
        if (JSON.stringify(newSelectedCanvas) !== JSON.stringify(current)) {
          setSelectedCanvas(newSelectedCanvas);
          localStorage.setItem('selectedCanvas', JSON.stringify(newSelectedCanvas));
        }
      }
    }
  }, [ownedCanvases, selectedCanvas.id]);

  useEffect(() => {
    if (!selectedCanvas.id && ownedCanvases.length > 0) {
      const defaultCanvas = ownedCanvases[0];
      const newSelectedCanvas = {
        id: defaultCanvas._id,
        type: defaultCanvas.type,
        name: defaultCanvas.name,
        currency: defaultCanvas.currency || '',
      };
      setSelectedCanvas(newSelectedCanvas);
      localStorage.setItem('selectedCanvas', JSON.stringify(newSelectedCanvas));
    }
  }, [ownedCanvases, selectedCanvas.id]);

  const updateSelectedCanvas = useCallback((canvas) => {
    if (!canvas) return;

    const newSelectedCanvas = {
      id: canvas._id,
      type: canvas.type,
      name: canvas.name,
      currency: canvas.currency || '',
    };

    setSelectedCanvas(newSelectedCanvas);
    localStorage.setItem('selectedCanvas', JSON.stringify(newSelectedCanvas));
  }, []);

  const value = useMemo(
    () => ({ selectedCanvas, updateSelectedCanvas }),
    [selectedCanvas, updateSelectedCanvas]
  );

  return (
    <CanvasContext.Provider value={value}>
      {children}
    </CanvasContext.Provider>
  );
};

export const useCanvas = () => {
  const context = useContext(CanvasContext);
  if (!context) {
    throw new Error('useCanvas must be used within a CanvasProvider');
  }
  return context;
};